// frontend/src/components/Schedules/AddBlockSchedule.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

function AddBlockSchedule() {
  const [blockSchedule, setBlockSchedule] = useState({
    name: '',
    duration: '',
    start_date: '',
    end_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [durationInfo, setDurationInfo] = useState({
    calendar_weeks: 0,
    actual_school_weeks: 0,
    holidays: []
  });
  const [isCalculating, setIsCalculating] = useState(false);
  const navigate = useNavigate();

  const calculateDuration = async (startDate, endDate) => {
    if (!startDate || !endDate) {
      setDurationInfo({ calendar_weeks: 0, actual_school_weeks: 0, holidays: [] });
      return;
    }

    setIsCalculating(true);
    try {
      const response = await axios.get('/schedules/api/block-schedules/calculate-duration/', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      const data = response.data;
      setDurationInfo({
        calendar_weeks: data.calendar_weeks,
        actual_school_weeks: data.actual_school_weeks,
        holidays: data.holidays
      });
      
      // Update the duration field with actual school weeks
      setBlockSchedule(prev => ({ ...prev, duration: data.actual_school_weeks }));
    } catch (error) {
      console.error('Error calculating duration:', error);
      setDurationInfo({ calendar_weeks: 0, actual_school_weeks: 0, holidays: [] });
      // Fallback to simple calculation if API fails
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (!isNaN(s) && !isNaN(e)) {
        const ms = e - s;
        if (ms >= 0) {
          const oneDay = 24 * 60 * 60 * 1000;
          const oneWeek = 7 * oneDay;
          const fallbackWeeks = Math.max(1, Math.ceil((ms + oneDay) / oneWeek));
          setBlockSchedule(prev => ({ ...prev, duration: fallbackWeeks }));
        }
      }
    } finally {
      setIsCalculating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedSchedule = { ...blockSchedule, [name]: value };
    setBlockSchedule(updatedSchedule);

    // Auto-calculate duration when both dates are available
    if (name === 'start_date' || name === 'end_date') {
      const startDate = name === 'start_date' ? value : updatedSchedule.start_date;
      const endDate = name === 'end_date' ? value : updatedSchedule.end_date;
      
      if (startDate && endDate) {
        calculateDuration(startDate, endDate);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    if (blockSchedule.start_date && blockSchedule.end_date) {
      const startDate = new Date(blockSchedule.start_date);
      const endDate = new Date(blockSchedule.end_date);
      if (endDate < startDate) {
        setError('End date must be on or after start date.');
        setSaving(false);
        return;
      }
    }

    try {
      const { name, start_date, end_date } = blockSchedule;
      await axios.post('/schedules/api/block-schedules/', {
        name,
        start_date,
        end_date,
        // duration is computed server-side in BlockSchedule.save()
      });
      navigate('/schedules/block-schedules');
    } catch (err) {
      console.error('Error adding block schedule:', err);
      setError('Failed to add block schedule.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container">
      <h1>Add Block Schedule</h1>
      <p>Create a time block used for scheduling (e.g., 10-week rotation).</p>

      {error && <p style={{ color: 'red' }}>{error}</p>}

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-row">
          <label htmlFor="name">Name</label>
          <input id="name" type="text" name="name" value={blockSchedule.name} onChange={handleChange} placeholder="e.g. Q1 10-week" required />
        </div>

        {/* Duration Information Display */}
        {(blockSchedule.start_date && blockSchedule.end_date) && (
          <div style={{ margin: '10px 0', padding: '15px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Duration Calculation</h4>
            {isCalculating ? (
              <p style={{ margin: '5px 0', color: '#666' }}>Calculating duration...</p>
            ) : (
              <div>
                <p style={{ margin: '5px 0' }}><strong>Calendar Weeks:</strong> {durationInfo.calendar_weeks}</p>
                <p style={{ margin: '5px 0' }}><strong>Actual School Weeks:</strong> {durationInfo.actual_school_weeks}</p>
                {durationInfo.holidays.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <p style={{ margin: '5px 0' }}><strong>Holidays in this period:</strong></p>
                    <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                      {durationInfo.holidays.map((holiday, index) => (
                        <li key={index} style={{ margin: '2px 0' }}>
                          {holiday.name} ({holiday.start_date} to {holiday.end_date})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="form-row">
          <label htmlFor="duration">Duration (weeks)</label>
          <input 
            id="duration" 
            type="number" 
            min="1" 
            name="duration" 
            value={blockSchedule.duration} 
            onChange={handleChange} 
            placeholder="Auto-calculated from dates" 
            readOnly={blockSchedule.start_date && blockSchedule.end_date}
            style={{
              backgroundColor: (blockSchedule.start_date && blockSchedule.end_date) ? '#f5f5f5' : 'white',
              cursor: (blockSchedule.start_date && blockSchedule.end_date) ? 'not-allowed' : 'text'
            }}
          />
          <small style={{ color: '#666' }}>This is auto-calculated from Start and End dates using holiday-aware logic.</small>
        </div>

        <div className="form-row">
          <label htmlFor="start_date">Start date</label>
          <input id="start_date" type="date" name="start_date" value={blockSchedule.start_date} onChange={handleChange} required />
        </div>

        <div className="form-row">
          <label htmlFor="end_date">End date</label>
          <input id="end_date" type="date" name="end_date" value={blockSchedule.end_date} onChange={handleChange} required />
        </div>

        <div className="actions-row">
          <button type="submit" className="primary-btn" disabled={saving}>{saving ? 'Saving...' : 'Add Block Schedule'}</button>
          <Link to="/schedules/block-schedules" className="secondary-btn">Cancel</Link>
        </div>
      </form>

      <style jsx>{`
        .form-card { background: #fff; border: 1px solid #e0e0e0; border-radius: 12px; padding: 16px; max-width: 520px; }
        .form-row { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; }
        label { font-weight: 600; color: #333; }
        input { padding: 10px 12px; border-radius: 8px; border: 1px solid #ccc; }
        .actions-row { display: flex; gap: 12px; margin-top: 16px; }
        .primary-btn, .secondary-btn { padding: 10px 14px; border-radius: 8px; text-decoration: none; cursor: pointer; border: 1px solid transparent; }
        .primary-btn { background: #1976d2; color: #fff; }
        .secondary-btn { background: #fff; color: #1976d2; border-color: #1976d2; }
      `}</style>
    </div>
  );
}

export default AddBlockSchedule;
